import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import {
	AfterContentInit,
	booleanAttribute,
	Component,
	EventEmitter,
	forwardRef,
	Input,
	input,
	OnInit,
	Output,
	Pipe,
	PipeTransform,
} from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	FormArray,
	FormControl,
	FormGroup,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
	Validators,
} from "@angular/forms";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { normalizeWorkspaceSchedule } from "@app/_helpers/normalize-workspace-schedule";
import { hasChange } from "@app/_helpers/utils";
import { CustomValidators } from "@app/_validators/custom-validators";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Logger } from "@timeghost/web-api";
import { isValid, parse as parseFromString, startOfWeek } from "date-fns/esm";
import { omit } from "lodash-es";
import { combineLatest, filter, map, Subject, startWith } from "rxjs";
import { Break } from "./due-breaks-control/due-breaks-control.component";
import { TargetHourDayValue } from "./target-hour-day-control/target-hour-day-control.component";

export interface WorkingHourSettings {
	breakBetweenWorkingDays: number;
	breaks: Break[];
	dailyMaxHours: number;
	minimumBreakDurationForCredit: number;
	preventDeviatingWorkingHours: boolean;
	averageWorkingHours: number;
	averageWorkingHoursRange: number;
}
export interface TargetHourValue {
	enabled?: boolean;
	from: Date;
	items: TargetHourDayValue[];
	name?: string;
	showItems?: boolean;
	workinghours: WorkingHourSettings;
}
export function createTargetHourGroup({ name }: Partial<{ name: string }> = {}, type: "user" | "workspace" = "user") {
	new Logger("createTargetHourGroup").debug({ name, type });
	return new FormGroup({
		...((type === "user" && {
			from: new FormControl<Date>(null, {
				updateOn: "blur",
			}),
			name: new FormControl(name || "New User Schedule"),
		}) ||
			{}),
		enabled: new FormControl<boolean>(false),
		items: new FormArray(
			new Array(7).fill(0).map((_, day) => {
				return new FormControl<TargetHourDayValue>({
					enabled: false,
					day,
					items: [
						{ from: "08:00", to: "12:00" },
						{ from: "13:00", to: "17:00" },
					],
				});
			})
		),
		workinghours: new FormGroup({
			dailyMaxHours: new FormControl("10:00", [CustomValidators.isNumeric]),
			breakBetweenWorkingDays: new FormControl("00:00", [CustomValidators.isNumeric]),
			minimumBreakDurationForCredit: new FormControl("00:15", [CustomValidators.isNumeric]),
			preventDeviatingWorkingHours: new FormControl<boolean>(false),
			averageWorkingHours: new FormControl(48, [CustomValidators.isNumeric]),
			averageWorkingHoursRange: new FormControl(24, [CustomValidators.isNumeric]),
			breaks: new FormArray([
				new FormGroup({
					minutesDue: new FormControl("00:30", [
						Validators.required,
						CustomValidators.validDateFormat("HH:mm"),
					]),
					workingHours: new FormControl("06:00", [
						Validators.required,
						CustomValidators.validDateFormat("HH:mm"),
					]),
				}),
				new FormGroup({
					minutesDue: new FormControl("00:45", [
						Validators.required,
						CustomValidators.validDateFormat("HH:mm"),
					]),
					workingHours: new FormControl("09:00", [
						Validators.required,
						CustomValidators.validDateFormat("HH:mm"),
					]),
				}),
			]),
		}),
	});
}
export type TargetHourGroupType = ReturnType<typeof createTargetHourGroup>["value"];

@Pipe({
	name: "viewFilterItems",
})
export class ViewFilterItemsPipe implements PipeTransform {
	transform(value: TargetHourDayValue["items"], ...args: any[]): any {
		if (!value || value.length <= 0) return null;
		return value.filter((d) => d.from && d.to);
	}
}
const log = new Logger("TargetHoursControlComponent");
@UntilDestroy()
@Component({
	selector: "tg-target-hours-control",
	templateUrl: "./target-hours-control.component.html",
	styleUrls: ["./target-hours-control.component.scss"],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TargetHoursControlComponent),
			multi: true,
		},
		{ provide: NG_VALIDATORS, useExisting: forwardRef(() => TargetHoursControlComponent), multi: true },
	],
})
export class TargetHoursControlComponent implements OnInit, AfterContentInit, ControlValueAccessor, Validator {
	@Input()
	type: "workspace" | "user" = "user";
	private _showDetails: boolean = false;
	@Input()
	get showDetails() {
		return this._showDetails;
	}
	set showDetails(val: BooleanInput) {
		this._showDetails = coerceBooleanProperty(val);
	}
	readonly disabled = input({ transform: booleanAttribute });
	readonly hideWorkingHours = input({ transform: booleanAttribute });

	@Input()
	defaultName: string = null;
	private _control = createTargetHourGroup({ name: this.defaultName }, this.type);
	readonly editing = new FormControl<boolean>(false);

	get control(): typeof this._control {
		return this._control;
	}
	get rawValue() {
		return this.control.getRawValue();
	}
	get itemsError() {
		return this.control.controls?.items?.controls.find((c) => c.errors)?.errors;
	}
	readonly onChange$ = new Subject<void>();
	readonly stats$ = combineLatest([this.control.valueChanges, this.onChange$.asObservable()]).pipe(
		untilDestroyed(this),
		startWith([this.control.value]),
		map(() => {
			const value = this.control.value;
			if (!value || !value.items?.length) return null;
			const total =
				value?.items
					?.reduce(
						(acc, r) => {
							if (r.enabled) acc.push(...r.items);
							return acc;
						},
						[] as TargetHourDayValue["items"]
					)
					?.reduce((acc, r) => {
						const start = parseFromString(r.from, "HH:mm", new Date()),
							end = parseFromString(r.to, "HH:mm", new Date()),
							ms = end.getTime() - start.getTime();
						return (acc += ms / 1000);
					}, 0) ?? 0;
			const msPerDay = value?.items?.reduce(
				(acc, r) => {
					if (acc[r.day] === undefined) acc[r.day] = 0;
					acc[r.day] += r.items.reduce((w, x) => {
						const start = parseFromString(x.from, "HH:mm", new Date()),
							end = parseFromString(x.to, "HH:mm", new Date()),
							ms = end.getTime() - start.getTime();
						return (w += ms / 1000);
					}, 0);
					return acc;
				},
				{} as { [key: number]: number }
			);
			return {
				total,
				days: msPerDay,
				enabled: !!value.enabled,
			};
		})
	);
	propagateChange: Function;
	propagateTouched: Function;
	private lastPropagatedValue: any;
	private toPropagatedValue(value: any) {
		return this.type === "user" ? value : normalizeWorkspaceSchedule(value);
	}
	get isWorkspaceSchedule() {
		return this.type === "workspace";
	}
	constructor(private dialog: MatDialog) {}
	validate(control: AbstractControl<any, any>): ValidationErrors {
		return this.control?.errors;
	}

	openCalPicker() {
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.control.value.from),
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.control.patchValue({
					from: x,
				});
			});
	}
	writeValue(value: typeof this.control.value): void {
		if (
			value?.workinghours?.breaks &&
			this.type === "workspace" &&
			this.control.controls.workinghours?.controls.breaks
		) {
			const breakControl = this.control.controls.workinghours.controls.breaks;
			if (value.workinghours.breaks?.length && value.workinghours.breaks.length !== breakControl.length) {
				breakControl.clear({ emitEvent: false });
				const toDelete = breakControl.length - value.workinghours.breaks.length;
				if (toDelete > 0) breakControl.controls.splice(breakControl.length - toDelete, toDelete);
				value.workinghours.breaks?.reduce((acc: FormArray, r) => {
					acc.push(
						new FormGroup({
							minutesDue: new FormControl(r.minutesDue, [
								Validators.required,
								CustomValidators.validDateFormat("HH:mm"),
							]),
							workingHours: new FormControl(r.workingHours, [
								Validators.required,
								CustomValidators.validDateFormat("HH:mm"),
							]),
						}),
						{ emitEvent: false }
					);
					return acc;
				}, breakControl);
			} else if (!value.workinghours.breaks?.length) breakControl.clear({ emitEvent: false });
		}
		if (this.isWorkspaceSchedule) {
			this.control.patchValue(omit(value, "name", "from"), { emitEvent: false, onlySelf: true });
		} else this.control.patchValue(value, { emitEvent: false, onlySelf: true });
		this.control.markAsPristine();
		this.lastPropagatedValue = this.toPropagatedValue(this.control.getRawValue());
	}
	registerOnChange(fn: any): void {
		this.propagateChange = (...args: any) => {
			this.onChange$.next();
			return fn(...args);
		};
	}
	registerOnTouched(fn: any): void {
		this.propagateTouched = (...args: any) => {
			this.onChange$.next();
			return fn(...args);
		};
	}
	setDisabledState?(isDisabled: boolean): void {
		if (isDisabled === this.control.disabled) return;
		this.control[isDisabled ? "disable" : "enable"]();
	}

	ngOnInit(): void {
		this._control = createTargetHourGroup({ name: this.defaultName }, this.type);
		this.control.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
			const nextValue = this.toPropagatedValue(this._control.getRawValue());
			if (!hasChange(this.lastPropagatedValue, nextValue)) return;
			this.lastPropagatedValue = nextValue;
			this.propagateChange?.(nextValue);
			this.propagateTouched?.(nextValue);
			log.debug("schedule hours", nextValue.items?.length, nextValue);
		});
		if (this.isWorkspaceSchedule) this.editing.patchValue(true);
	}
	ngAfterContentInit(): void {}

	formatFrom(date: Date) {
		if (!date) return null;
		return new Date(date);
	}
	editItem() {
		const value = this.control.getRawValue();
		if (!value) return;
		this.editing.patchValue(!this.editing.value);
	}
	saveItem() {
		const value = this.control.getRawValue();
		if (!value) return;
		this.editing.patchValue(false, { emitEvent: false });
		this.control.controls.items.controls.forEach((c) => {
			const newValue = {
				...c.value,
				items: c.value.items.map((d) => ({ ...d, editing: false })),
			};

			c.setValue(newValue, { emitEvent: false });
		});
	}
	private pushChange(value: any) {
		const normalized = this.toPropagatedValue(value);
		if (!hasChange(this.lastPropagatedValue, normalized)) return;
		this.lastPropagatedValue = normalized;
		this.propagateChange?.(normalized);
		this.propagateTouched?.(normalized);
	}
	@Output()
	private onDelete = new EventEmitter(true);
	@Output()
	private onCopy = new EventEmitter(true);
	delItem() {
		this.onDelete.emit(this.control.value);
	}
	cpyItem() {
		this.onCopy.emit(this.control.value);
	}
	readonly now = startOfWeek(new Date(), { weekStartsOn: 1 });
}
